import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { PricingCard } from "@/components/shared/PricingCard";
import { ProcessStep } from "@/components/shared/ProcessStep";
import { 
  ArrowRight, 
  Check, 
  MapPin, 
  Scale, 
  UserCheck, 
  Shield, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Users,
  Sparkles,
} from "lucide-react";

const Services = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="section-padding bg-primary-light">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-6">
              Services & Pricing
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Transparent, tiered pricing designed to meet your practice where you are. 
              From individual insights to fully facilitated partnership sessions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Pricing Tiers */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Core Services"
            title="Choose your level of support"
            description="Each level builds on the previous, providing progressively deeper insight and support."
          />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <PricingCard
              level="Individual"
              title="Leadership & Values Inventory"
              price="£119"
              priceNote="per person"
              description="Personal leadership insights with automated reporting. The foundation for all partnership work."
              features={[
                "Comprehensive individual assessment",
                "Automated personal report",
                "Leadership style analysis",
                "Values and priorities identification",
                "Governance preferences",
                "Immediate digital delivery",
              ]}
              cta="Start the Inventory"
              ctaLink="/start-inventory"
            />
            <PricingCard
              level="Practice Bundle"
              title="6 Inventory Tokens"
              price="£600"
              priceNote="save £114"
              description="For small to medium partnerships wanting team insights."
              features={[
                "6 individual assessment tokens",
                "Share with team members",
                "Each person gets unique code",
                "All reports emailed individually",
                "Tokens never expire",
                "Best for partnerships of 4-6",
              ]}
              cta="Purchase Bundle"
              ctaLink="/purchase"
              highlighted
              badge="Most Popular"
            />
            <PricingCard
              level="Practice Plus"
              title="10 Inventory Tokens"
              price="£1,000"
              priceNote="save £190"
              description="For larger partnerships or extended leadership teams."
              features={[
                "10 individual assessment tokens",
                "Share across your organisation",
                "Each person gets unique code",
                "All reports emailed individually",
                "Best value per token",
                "Ideal for larger partnerships",
              ]}
              cta="Purchase Bundle"
              ctaLink="/purchase"
            />
          </div>
        </div>
      </section>

      {/* Facilitated Sessions */}
      <section className="section-padding bg-secondary/30">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Premium Service"
            title="Facilitated Partnership Sessions"
            description="Expert facilitation for practices wanting maximum impact and professional guidance."
          />

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            <div className="card-premium">
              <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                Half-Day Facilitation
              </h3>
              <p className="text-2xl font-bold text-primary mb-2">£2,400</p>
              <p className="text-sm text-muted-foreground mb-4">
                3-4 hours of focused facilitation
              </p>
              <ul className="space-y-2">
                {[
                  "Partnership analysis report",
                  "Focused agenda on priority issues",
                  "Core alignment exercises",
                  "Action planning session",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-premium">
              <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                Full-Day Facilitation
              </h3>
              <p className="text-2xl font-bold text-primary mb-2">£3,800</p>
              <p className="text-sm text-muted-foreground mb-4">
                6-7 hours of comprehensive facilitation
              </p>
              <ul className="space-y-2">
                {[
                  "Partnership analysis report",
                  "Comprehensive agenda",
                  "Deep-dive alignment work",
                  "Governance review and planning",
                  "Extended action planning",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Travel note */}
          <div className="max-w-2xl mx-auto mt-8">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-muted">
              <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Travel costs:</strong> Add £500–£1,000 for facilitated sessions 
                outside Greater London, covering travel and accommodation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Process */}
      <section className="section-padding bg-secondary/30">
        <div className="container-wide">
          <SectionHeader
            eyebrow="The Process"
            title="How it works"
            description="A clear, structured process designed to meet your practice where you are."
          />

          <div className="max-w-3xl mx-auto space-y-12">
            <ProcessStep
              number={1}
              title="Discovery conversation"
              description="We start with a free, no-obligation call to understand your practice, your goals, and any specific challenges you're facing. This helps us recommend the right level of support and tailor our approach."
            />
            <ProcessStep
              number={2}
              title="Individual inventories"
              description="Each partner completes the Leadership & Values Inventory independently and confidentially. The assessment takes 20-30 minutes and can be completed at each person's convenience."
            />
            <ProcessStep
              number={3}
              title="Personal reports delivered"
              description="Each partner receives their own confidential report immediately upon completion. These personal insights are valuable regardless of whether you proceed to partnership-level analysis."
            />
            <ProcessStep
              number={4}
              title="Partnership analysis"
              description="For Level 2 and 3 services, we aggregate individual results into a partnership report revealing collective patterns, alignment areas, and opportunities for improvement."
            />
            <ProcessStep
              number={5}
              title="Facilitation or self-service"
              description="Choose self-facilitation with our comprehensive workshop toolkit, or book expert facilitation for maximum impact. Either way, you'll have structured tools for meaningful conversations."
            />
            <ProcessStep
              number={6}
              title="Action and follow-up"
              description="Translate insights into concrete actions. Governance improvements, communication agreements, role clarity—whatever your practice needs to perform at its best."
            />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Timeline"
            title="What to expect"
          />

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            <div className="card-premium text-center">
              <Clock className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Level 1
              </h3>
              <p className="text-sm text-muted-foreground">
                20-30 minutes per person. Reports delivered immediately.
              </p>
            </div>
            <div className="card-premium text-center">
              <Users className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Level 2
              </h3>
              <p className="text-sm text-muted-foreground">
                1-2 weeks from start to partnership report, depending on partner availability.
              </p>
            </div>
            <div className="card-premium text-center">
              <Sparkles className="h-10 w-10 text-primary mx-auto mb-4" />
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Level 3
              </h3>
              <p className="text-sm text-muted-foreground">
                2-4 weeks from initial contact to facilitated session, based on scheduling.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality */}
      <section className="py-12 md:py-16 bg-primary-light">
        <div className="container-narrow text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              Confidentiality Guaranteed
            </span>
          </div>
          <p className="text-lg text-foreground leading-relaxed max-w-2xl mx-auto">
            Individual responses are <strong>never</strong> shared with other partners. 
            Personal reports are confidential. Partnership reports contain only aggregated 
            data that protects individual anonymity while revealing collective patterns.
          </p>
        </div>
      </section>

      {/* Partner Selection Tool */}
      <section className="section-padding bg-background">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Partner Selection Tool"
            title="Structured insight for fair partner selection"
            description="When considering new partners, you need more than CVs and interviews."
          />

          {/* Important Clarification */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="flex items-start gap-4 p-6 rounded-xl bg-muted border border-border">
              <AlertCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">
                  This is not a hiring decision tool
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  The Partner Selection & Fit Tool provides structured insight to inform and support 
                  your partnership decisions—it does not make recommendations or automate hiring decisions. 
                  Professional judgement remains entirely with you and your practice. All assessments 
                  are designed to support fair, non-discriminatory processes.
                </p>
              </div>
            </div>
          </div>

          {/* What It Provides */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto mb-12">
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <Scale className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Values Alignment
              </h3>
              <p className="text-sm text-muted-foreground">
                Compare candidate values with your existing partnership profile to identify areas 
                of alignment and potential difference.
              </p>
            </div>
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <UserCheck className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Leadership Compatibility
              </h3>
              <p className="text-sm text-muted-foreground">
                Understand how candidate leadership styles might complement or contrast 
                with your current partnership dynamics.
              </p>
            </div>
            <div className="card-premium text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center mx-auto mb-4">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Governance Expectations
              </h3>
              <p className="text-sm text-muted-foreground">
                Explore candidate expectations around decision-making, accountability, 
                and partnership structure.
              </p>
            </div>
          </div>

          {/* Partner Selection Process */}
          <div className="max-w-3xl mx-auto space-y-6 mb-12">
            <h3 className="font-display text-xl font-semibold text-foreground text-center mb-6">
              How partner selection works
            </h3>
            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Establish your partnership baseline
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Existing partners complete the Leadership & Values Inventory to create 
                    an aggregate partnership profile.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Candidate assessment
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Prospective partners complete the same Inventory as part of your selection process.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Comparative insight report
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Receive structured insight comparing candidate profiles with your partnership baseline—
                    areas of alignment, difference, and potential conversation points.
                  </p>
                </div>
              </div>
            </div>
            <div className="card-premium">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Informed conversations
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    Use insights to guide interview questions and partnership discussions, 
                    ensuring thorough, fair evaluation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                title: "Defensible process",
                description: "A structured, documented approach that supports fair and non-discriminatory selection.",
              },
              {
                title: "Deeper insight",
                description: "Go beyond CVs and interviews to understand values, leadership style, and governance expectations.",
              },
              {
                title: "Better conversations",
                description: "Structured insights provide meaningful topics for partner interviews and discussions.",
              },
              {
                title: "Reduced risk",
                description: "Identify potential misalignments before they become partnership problems.",
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-narrow text-center text-primary-foreground">
          <h2 className="font-display text-3xl font-semibold mb-4">
            Not sure which level is right for you?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
            Book a free, no-obligation discovery call to discuss your practice's needs 
            and get a personalised recommendation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="xl">
                Book a Discovery Call
              </Button>
            </Link>
            <Link to="/start-inventory">
              <Button variant="outline" size="xl" className="border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                Start the Inventory
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
